import React, { useEffect, useCallback, useRef } from 'react'
import { track } from '../../../analytics'

/**
 * CompleteStep – shown after registration completes.
 *
 * Props:
 *   leagueName        – name of the registered league
 *   onReset           – callback to start a fresh registration
 *   onViewLeagues     – navigate to My Leagues
 *   onViewDashboard   – navigate to Dashboard
 */
function CompleteStep({
  leagueName,
  nominatedPartner,
  onReset,
  onViewLeagues,
  onViewDashboard,
}) {
  const hasNavigatedRef = useRef(false)

  // Track registration completion when component mounts
  useEffect(() => {
    try {
      track('registration_completed', {
        league_name: leagueName,
        source: 'multi_step_registration',
      })
    } catch (err) {
      console.error('Analytics tracking error:', err)
    }
  }, [leagueName])

  const handleViewDashboard = useCallback(() => {
    if (hasNavigatedRef.current) return
    hasNavigatedRef.current = true
    try {
      track('navigation.dashboard', { source: 'registration_complete' })
    } catch (err) {
      console.error('Analytics tracking error:', err)
    }
    onViewDashboard()
  }, [onViewDashboard])

  const handleViewLeagues = useCallback(() => {
    if (hasNavigatedRef.current) return
    hasNavigatedRef.current = true
    try {
      track('navigation.my_leagues', { source: 'registration_complete' })
    } catch (err) {
      console.error('Analytics tracking error:', err)
    }
    onViewLeagues()
  }, [onViewLeagues])

  const handleRegisterAnother = () => {
    if (hasNavigatedRef.current) return
    hasNavigatedRef.current = true
    try {
      track('navigation.register', {
        source: 'registration_complete',
        action: 'register_another',
      })
    } catch (err) {
      console.error('Analytics tracking error:', err)
    }
    onReset()
  }

  return (
    <div className="registration-step complete-step">
      <div className="success-icon">✓</div>
      <h4 className="step-title">Registration Complete!</h4>
      <p className="step-description">
        Congratulations! You&apos;re now registered for {leagueName}.
      </p>

      {nominatedPartner && (
        <p className="step-description">
          {nominatedPartner.label} has been registered as your doubles partner
          and will receive a notification.
        </p>
      )}

      <div className="complete-actions">
        <button
          className="btn btn-primary btn-lg w-100 mb-2"
          onClick={handleViewDashboard}
          aria-label="Return to dashboard"
        >
          Return to Dashboard
        </button>
        <button
          className="btn btn-outline-primary w-100 mb-2"
          onClick={handleViewLeagues}
          aria-label="View my leagues"
        >
          View My Leagues
        </button>
        <button
          className="btn btn-outline-secondary w-100"
          onClick={handleRegisterAnother}
          aria-label="Register for another league"
        >
          Register for Another League
        </button>
      </div>
    </div>
  )
}

export default CompleteStep
